import OrganizationDetailModal from "@/features/onboarding/components/Onboarding/OrganizationDetailModal";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { ArrowLeft, Building2 } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMainStore } from "@/stores";
import { getOrganizationByEmail } from "@/api/auth";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { handleReturnToSignUp } from "@/shared/utils/helperFunctions";

const JoinOrganization = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [org, setOrg] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useMainStore();

  useEffect(() => {
    const fetchOrg = async () => {
      if (!user?.email) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await getOrganizationByEmail(user.email);
        setOrg(res.organization);
        setError(null);
      } catch (err) {
        setError(
          err.response?.data?.error ||
          err.message ||
          "Failed to load organization",
        );
        setOrg(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrg();
  }, [user?.email]);

  // Org doesn't exist
  useEffect(() => {
    if (!isLoading && (error || !org) && user?.email) {
      navigate("/admin-request", { replace: true });
    }
  }, [isLoading, error, org, user?.email, navigate]);

  const orgDetails = org
    ? [
      { title: "Organization", value: org.name || "—" },
      {
        title: "Members",
        value: org.user_count != null ? `${org.user_count} members` : "—",
      },
      { title: "Your Email", value: user?.email || "—" },
    ]
    : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[100dvh] w-full">
        <Loader text="Loading organization..." />
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="flex items-center justify-center h-[100dvh] w-full">
        <Loader text="Redirecting..." />
      </div>
    );
  }

  return (
    <div className="bg-[#F9FAFB] min-h-[100dvh] w-full flex flex-col overflow-y-auto">

      {/* Back button — sticks to top-left on all sizes */}
      <div className="w-full px-4 sm:px-6 pt-4 sm:pt-5 flex-shrink-0">
        <div
          className="flex items-center gap-1.5 cursor-pointer w-fit"
          onClick={() => handleReturnToSignUp(navigate)}
        >
          <ArrowLeft
            strokeWidth={1.5}
            size={20}
            className="text-secondary"
          />
          <button className="text-secondary font-normal text-[0.85rem] hover:underline transition-all duration-300">
            Return to Login
          </button>
        </div>
      </div>

      {/* Main content — centers vertically when space allows */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-5">
        <div
          className="border border-gray-200 rounded-2xl p-5 sm:p-6
        bg-white flex flex-col gap-4 shadow-sm
        w-full max-w-[512px]"
        >
          {/* Icon */}
          <div className="flex items-center justify-center rounded-full p-3.5 bg-[#207C971A] w-fit">
            <Building2 className="text-secondary" size={22} />
          </div>

          {/* Heading / para */}
          <div className="flex flex-col gap-2">
            <h1 className="text-gray-800 text-[1.2rem] sm:text-[1.38rem] leading-7 sm:leading-8 font-medium">
              Join Your Organization
            </h1>
            <p className="text-gray-500 text-[0.85rem] leading-5 font-normal">
              Your company{" "}
              <span className="font-bold">{org.name}</span> already has a
              SalesWorx workspace. Request access from your organization admin
              to get started.
            </p>
          </div>

          {/* Org details */}
          <div className="bg-gray-50 rounded-[10px] p-4 sm:p-[22px] flex flex-col gap-3.5">
            {orgDetails?.map((org, index) => (
              <div key={index} className="flex gap-3 items-start">
                <div className="flex flex-col">
                  <h5 className="text-gray-500 text-[0.7rem] leading-[18px] font-normal">
                    {org.title}
                  </h5>
                  <p className="text-gray-800 text-[0.85rem] leading-5 font-normal break-words">
                    {org.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2.5">
            <SecondaryButton
              title="View Organization Details"
              onClick={() => setIsOpen(true)}
              className="shadow-sm"
            />
            <PrimaryButton
              startContent={false}
              children="Request Access"
              onClick={() =>
                navigate("/join-organization/confirm", { state: { org } })
              }
            />
          </div>

          {/* Admin info */}
          <div className="flex flex-col text-secondary p-4 bg-[#207C971A] rounded-[10px] border border-secondary">
            <p className="text-[0.85rem] leading-5 font-normal">
              Your organization admin will receive an email notification and
              can approve your access from their team management settings.
            </p>
          </div>

          {/* Divider */}
          <div className="h-[0.5px] bg-gray-200" />

          <p className="text-gray-500 text-[0.7rem] text-center leading-[18px] font-normal">
            Not the right organization?{" "}
            <span className="text-secondary cursor-pointer hover:underline transition-all duration-300">
              Contact Support
            </span>
          </p>
        </div>
      </div>

      <OrganizationDetailModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        org={org}
      />
    </div>
  );
};

export default JoinOrganization;
